import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import {
  IconGraduation,
  IconHearts,
  IconIdCard,
  IconMessage,
  IconSearch,
} from "../components/Icons";
import { journeySteps } from "../data";

type JourneySectionProps = {
  journeyStep: number;
};

const journeyIcons = {
  search: IconSearch,
  cap: IconGraduation,
  id: IconIdCard,
  hearts: IconHearts,
  chat: IconMessage,
} as const;

export const JourneySection = ({ journeyStep }: JourneySectionProps) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [journeyStyle, setJourneyStyle] = useState<CSSProperties>({});

  useEffect(() => {
    const update = () => {
      const track = trackRef.current;
      if (!track) {
        return;
      }

      const trackWidth = track.offsetWidth;
      const isNarrow = window.matchMedia("(max-width: 640px)").matches;
      const gap = isNarrow ? 12 : 16;
      const itemWidth = isNarrow
        ? Math.round(
            trackWidth / 2 + 44 * (1 - 2 * 0.3) - gap
          )
        : Math.round(trackWidth * 0.7);
      const translate = -journeyStep * (itemWidth + gap);

      setJourneyStyle({
        "--journey-item-width": `${itemWidth}px`,
        "--journey-gap": `${gap}px`,
        "--journey-translate": `${translate}px`,
      } as CSSProperties);
    };

    update();
    window.addEventListener("resize", update);

    return () => window.removeEventListener("resize", update);
  }, [journeyStep]);

  return (
    <section className="sg-section sg-journey">
      <div className="sg-container">
        <div className="sg-section__header">
          <span className="sg-kicker">Your Journey</span>
          <h2 className="sg-section__title sg-section__title--magenta">
            From Inspiration to Connection
          </h2>
          <p className="sg-section__subtitle">
            Five steps to finding a designer who truly understands your vision.
          </p>
        </div>

      <div
        className="sg-journey__track"
        ref={trackRef}
        style={journeyStyle}
      >
        <div className="sg-journey__line" />
          <div className="sg-journey__items">
            {journeySteps.map((step, index) => {
              const StepIcon = journeyIcons[step.icon];
              const isActive = index === journeyStep;

              return (
                <div
                  key={step.title}
                  className={[
                    "sg-journey__step",
                    isActive ? "is-active" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <div className="sg-journey__circle">
                    <StepIcon size={40} />
                  </div>
                  <div className="sg-journey__label">{step.title}</div>
                  <div className="sg-journey__desc">{step.description}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
